import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDED = ["4 × LumaDeck solar lights", "Mounting screws + adhesive pads", "1-year warranty card", "Quick-start guide"];

export const OrderForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/orders`, form);
      setPlaced(true);
      toast.success("Order placed! We'll call to confirm delivery.");
    } catch (err) {
      toast.error("Could not place the order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-stone-900/70 border border-white/10 px-5 py-4 text-stone-100 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-400/40 transition-colors duration-300";

  return (
    <section id="order" className="py-32 sm:py-40 border-t border-white/10 relative overflow-hidden" data-testid="order-section">
      <div className="absolute top-0 left-1/3 w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-[140px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="relative overflow-hidden rounded-2xl aspect-[4/3] clip-frame group mb-10">
            <img
              src="/images/pack.png"
              alt="LumaDeck 4-pack with retail box"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              data-testid="order-pack-image"
            />
            <div className="absolute top-5 right-5 rounded-full bg-amber-400 text-stone-950 px-4 py-2 text-sm font-semibold" data-testid="order-price-badge">
              ₹1,499
            </div>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-50 mb-4" data-testid="order-heading">
            The 4-pack. <span className="italic text-amber-400">One evening, transformed.</span>
          </h2>
          <ul className="space-y-3 mt-8">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-center gap-3 text-stone-300">
                <Check className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border border-white/10 rounded-3xl p-8 sm:p-12 bg-stone-950/50 backdrop-blur-xl"
        >
          {placed ? (
            <div className="text-center py-16" data-testid="order-success">
              <div className="w-16 h-16 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mx-auto mb-8">
                <Check className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="font-serif text-3xl text-stone-50 mb-4">You're all set.</h3>
              <p className="text-stone-400 max-w-sm mx-auto">
                Your LumaDeck 4-pack order is in. We'll call {form.phone} to confirm delivery details.
              </p>
              <button
                onClick={() => { setPlaced(false); setForm({ name: "", phone: "", address: "" }); }}
                className="mt-10 text-sm text-amber-400 hover:text-amber-300 transition-colors duration-300"
                data-testid="order-again-button"
              >
                Place another order
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="order-form">
              <p className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-2">Cash on delivery</p>
              <h3 className="font-serif text-3xl text-stone-50 mb-8">Reserve your pack</h3>
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="order-name-input" />
              <input required placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="order-phone-input" type="tel" />
              <textarea required placeholder="Delivery address" value={form.address} onChange={set("address")} rows={3} className={`${inputCls} resize-none`} data-testid="order-address-input" />
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-amber-400 text-stone-950 py-4 font-medium text-lg transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
                data-testid="order-submit-button"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? "Placing order…" : "Order now — ₹1,499"}
              </button>
              <p className="text-xs text-stone-500 text-center">Free shipping across India · Pay on delivery</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
